import { Check, ChevronDown, ChevronRight, Copy, Maximize2, Minimize2 } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";

import { Button } from "../ui/button.js";
import { cn } from "../../lib/cn.js";

/**
 * Collapsible, type-coloured JSON tree with copy-to-clipboard and
 * expand-all / collapse-all controls.
 *
 * Designed for the vision-run / capture-analysis pages where engineers
 * need to read deeply-nested payloads (per-detection bboxes, shelf
 * sections, thresholds blocks). Each non-leaf node remembers its own
 * expanded state via useState; the global "expand all" / "collapse all"
 * buttons increment counters that the nodes watch with useEffect.
 *
 * The component takes any JSON-serialisable value. Non-serialisable
 * inputs (functions, Symbols, circular refs) are coerced to JSON via
 * JSON.parse(JSON.stringify(value)) on a defensive boundary, since the
 * upstream payload always comes from the server already-stringified.
 *
 * Performance note: this renders synchronously for moderately-sized
 * payloads (~10k nodes). For larger blobs, the caller should virtualize
 * the surrounding scroll container — we don't ship a virtualizer here
 * because the typical payload is under that ceiling.
 */
export interface JsonViewerProps {
  value: unknown;
  /** Auto-expand depth on first render (default 2 — root + one level). */
  defaultExpandDepth?: number;
  /** Optional cap on the scroll container height. e.g. "28rem". */
  maxHeight?: string;
  /** Extra header content rendered to the left of the action buttons. */
  headerLeft?: ReactNode;
  /**
   * Top-level object keys to visually highlight (e.g. the fields that changed
   * in an audit diff). Only the root object's entries are matched.
   */
  highlightKeys?: ReadonlySet<string>;
}

interface ExpandSignals {
  expandSeq: number;
  collapseSeq: number;
  defaultExpandDepth: number;
  highlightKeys: ReadonlySet<string>;
}

const EMPTY_HIGHLIGHT: ReadonlySet<string> = new Set();

const ExpandSignalsContext = createContext<ExpandSignals>({
  expandSeq: 0,
  collapseSeq: 0,
  defaultExpandDepth: 2,
  highlightKeys: EMPTY_HIGHLIGHT,
});

export function JsonViewer({
  value,
  defaultExpandDepth = 2,
  maxHeight,
  headerLeft,
  highlightKeys,
}: JsonViewerProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [expandSeq, setExpandSeq] = useState(0);
  const [collapseSeq, setCollapseSeq] = useState(0);
  const copyTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  const formatted = useMemo(() => safeStringify(value), [value]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(formatted).then(() => {
      setCopied(true);
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
    });
  }, [formatted]);

  const expandAll = useCallback(() => setExpandSeq((n) => n + 1), []);
  const collapseAll = useCallback(() => setCollapseSeq((n) => n + 1), []);

  const signals = useMemo<ExpandSignals>(
    () => ({
      expandSeq,
      collapseSeq,
      defaultExpandDepth,
      highlightKeys: highlightKeys ?? EMPTY_HIGHLIGHT,
    }),
    [expandSeq, collapseSeq, defaultExpandDepth, highlightKeys],
  );

  return (
    <div className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-2">
        <div className="min-w-0 flex-1 text-xs text-muted-foreground">{headerLeft}</div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onClick={expandAll}
            aria-label="Expand all"
            title="Expand all"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onClick={collapseAll}
            aria-label="Collapse all"
            title="Collapse all"
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onClick={handleCopy}
            aria-label={copied ? "Copied" : "Copy JSON"}
            title={copied ? "Copied" : "Copy JSON"}
          >
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      <div
        className="overflow-auto px-4 py-3 font-mono text-xs leading-relaxed"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <ExpandSignalsContext.Provider value={signals}>
          <JsonNode value={value} depth={0} />
        </ExpandSignalsContext.Provider>
      </div>
    </div>
  );
}

// ── internal: recursive node ────────────────────────────────────────

interface JsonNodeProps {
  value: unknown;
  depth: number;
}

function JsonNode({ value, depth }: JsonNodeProps): JSX.Element {
  const { expandSeq, collapseSeq, defaultExpandDepth, highlightKeys } =
    useContext(ExpandSignalsContext);
  const [open, setOpen] = useState(depth < defaultExpandDepth);

  // Watch global expand / collapse signals. We compare the previous value
  // to the new one so the initial mount doesn't override the
  // depth-based default.
  const initialMount = useRef(true);
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    setOpen(true);
  }, [expandSeq]);
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    setOpen(false);
  }, [collapseSeq]);

  if (value === null) return <Primitive kind="null">null</Primitive>;
  if (typeof value === "boolean") {
    return <Primitive kind="boolean">{String(value)}</Primitive>;
  }
  if (typeof value === "number") {
    return <Primitive kind="number">{String(value)}</Primitive>;
  }
  if (typeof value === "string") {
    return <Primitive kind="string">{JSON.stringify(value)}</Primitive>;
  }

  // Array
  if (Array.isArray(value)) {
    const empty = value.length === 0;
    return (
      <span className="inline">
        <CollapseToggle open={open} onToggle={() => setOpen((v) => !v)} disabled={empty} />
        <Bracket>{"["}</Bracket>
        {empty ? (
          <Bracket>{"]"}</Bracket>
        ) : open ? (
          <>
            <div className="border-l border-border/50 pl-3">
              {value.map((child, i) => (
                <div key={i} className="flex">
                  <span className="flex-1 min-w-0">
                    <JsonNode value={child} depth={depth + 1} />
                    {i < value.length - 1 ? <Bracket>{","}</Bracket> : null}
                  </span>
                </div>
              ))}
            </div>
            <Bracket>{"]"}</Bracket>
          </>
        ) : (
          <>
            <Muted>{` ${value.length} item${value.length === 1 ? "" : "s"} `}</Muted>
            <Bracket>{"]"}</Bracket>
          </>
        )}
      </span>
    );
  }

  // Object
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    const empty = entries.length === 0;
    return (
      <span className="inline">
        <CollapseToggle open={open} onToggle={() => setOpen((v) => !v)} disabled={empty} />
        <Bracket>{"{"}</Bracket>
        {empty ? (
          <Bracket>{"}"}</Bracket>
        ) : open ? (
          <>
            <div className="border-l border-border/50 pl-3">
              {entries.map(([k, v], i) => {
                // Only the root object's keys map to audit-diff fields.
                const highlighted = depth === 0 && highlightKeys.has(k);
                return (
                  <div
                    key={k}
                    className={cn(
                      "flex rounded-sm",
                      highlighted && "bg-amber-500/10 ring-1 ring-inset ring-amber-500/25",
                    )}
                  >
                    <span className="flex-1 min-w-0">
                      <Key highlighted={highlighted}>{JSON.stringify(k)}</Key>
                      <Bracket>{": "}</Bracket>
                      <JsonNode value={v} depth={depth + 1} />
                      {i < entries.length - 1 ? <Bracket>{","}</Bracket> : null}
                    </span>
                  </div>
                );
              })}
            </div>
            <Bracket>{"}"}</Bracket>
          </>
        ) : (
          <>
            <Muted>{` ${entries.length} key${entries.length === 1 ? "" : "s"} `}</Muted>
            <Bracket>{"}"}</Bracket>
          </>
        )}
      </span>
    );
  }

  // Defensive fallback for anything not JSON-shaped (BigInt, function,
  // Symbol). Coerce to a string so the viewer never throws.
  return <Primitive kind="null">{String(value)}</Primitive>;
}

// ── leaves ──────────────────────────────────────────────────────────

function CollapseToggle({
  open,
  onToggle,
  disabled,
}: {
  open: boolean;
  onToggle: () => void;
  disabled: boolean;
}): JSX.Element {
  if (disabled) return <span className="mr-0.5 inline-block w-3" aria-hidden="true" />;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={open ? "Collapse" : "Expand"}
      className="mr-0.5 inline-flex align-middle text-muted-foreground hover:text-foreground"
    >
      {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
    </button>
  );
}

function Key({
  children,
  highlighted,
}: {
  children: ReactNode;
  highlighted?: boolean;
}): JSX.Element {
  return (
    <span
      className={cn(
        "text-foreground",
        highlighted && "font-semibold text-amber-700 dark:text-amber-400",
      )}
    >
      {children}
    </span>
  );
}

function Bracket({ children }: { children: ReactNode }): JSX.Element {
  return <span className="text-muted-foreground">{children}</span>;
}

function Muted({ children }: { children: ReactNode }): JSX.Element {
  return <span className="text-muted-foreground/70">{children}</span>;
}

type PrimitiveKind = "string" | "number" | "boolean" | "null";

function Primitive({ kind, children }: { kind: PrimitiveKind; children: ReactNode }): JSX.Element {
  return (
    <span
      className={cn(
        kind === "string" && "text-emerald-700 dark:text-emerald-400",
        kind === "number" && "text-amber-700 dark:text-amber-400",
        kind === "boolean" && "text-violet-700 dark:text-violet-400",
        kind === "null" && "text-muted-foreground italic",
      )}
    >
      {children}
    </span>
  );
}

// ── helpers ─────────────────────────────────────────────────────────

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
